<template>
  <!-- The persistent `#dokumentasi-kegiatan` anchor wrapper lives in the page
       shell, so the "Lihat Dokumentasi" CTA still resolves on a cold native
       load and when this preview has nothing to show. -->
  <section
    v-if="documentations.length || isEmpty"
    class="gr-section gr-section--panel gr-docs"
  >
    <div class="gr-container">
      <header class="gr-head">
        <p class="gr-eyebrow" aria-hidden="true">
          <span class="gr-eyebrow__index">05</span>
          <span class="gr-eyebrow__rule" />
        </p>
        <h2 class="gr-h2">Dokumentasi Kegiatan</h2>
      </header>

      <div v-if="documentations.length" class="gr-docs__sheet">
        <article
          v-for="documentation in documentations"
          :key="documentation.uuid"
          class="gr-docs__frame"
        >
          <button
            type="button"
            class="gr-docs__link"
            @click="openDocumentation(documentation)"
          >
            <div class="gr-docs__media">
              <img
                v-if="
                  documentation.photos[0]?.imageUrl &&
                  !failedImages.has(documentation.uuid)
                "
                :ref="(element) => setImageElement(documentation.uuid, element)"
                :src="documentation.photos[0].imageUrl"
                :alt="documentation.photos[0].altText"
                class="gr-docs__img"
                loading="lazy"
                decoding="async"
                @error="markImageError(documentation.uuid)"
              />
              <div
                v-else
                class="gr-docs__img gr-docs__img--fallback"
                aria-label="Gambar tidak tersedia"
              />
            </div>
            <div class="gr-docs__caption">
              <h3 class="gr-docs__title">{{ documentation.title }}</h3>
              <p class="gr-docs__meta">
                {{ formatDocumentationDate(documentation.documentationDate) }}
                ·
                {{ documentation.documentationLocation }}
              </p>
            </div>
          </button>
        </article>
      </div>

      <div v-else class="gr-empty">
        <v-icon
          icon="mdi-image-multiple-outline"
          size="20"
          aria-hidden="true"
        />
        <p class="gr-empty__text">Belum ada dokumentasi yang dipublikasikan.</p>
      </div>

      <DocumentationDialog
        :open="isDialogOpen"
        :uuid="activeUuid"
        :summary="activeSummary"
        @close="closeDocumentation"
      />

      <div class="gr-docs__cta">
        <NuxtLink
          to="/documentation"
          class="gr-btn gr-btn--solid gr-btn--on-panel"
        >
          Lihat semua dokumentasi
          <v-icon icon="mdi-arrow-right" size="18" aria-hidden="true" />
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import DocumentationDialog from "@/features/documentation/views/components/DocumentationDialog.vue";
import {
  documentationQuery,
  formatDocumentationDate,
  getDocumentationRows,
  type DocumentationResponse,
  type PublicDocumentation,
} from "~/features/documentation/documentation";

const { $customFetch } = useNuxtApp();

const { data, error, refresh } = useAsyncData(
  "site:documentation-preview",
  () =>
    $customFetch<DocumentationResponse>(
      `/public-documentations?${documentationQuery({ page: 1, size: 3 }, null)}`,
    ),
);

// A cached error result (a transient SSR failure, say) otherwise sticks for
// this key across client-side navigations, and nothing else here can recover
// it. Retry once on the client.
onMounted(() => {
  if (error.value) refresh();
});

// The candidate has no documentation detail route: a record opens in place.
const isDialogOpen = ref(false);
const activeUuid = ref<string | null>(null);
const activeSummary = ref<PublicDocumentation | null>(null);

// The dialog takes a one-way `:open` prop plus a `@close` event rather than
// `v-model`, so Vuetify never sees the binding it uses to restore focus and
// drops it on `<body>` when the dialog closes. A keyboard user who pressed
// Escape would then have to tab from the top of the document to get back to
// what they opened (WCAG 2.4.3). Hold the trigger ourselves.
let dialogTrigger: HTMLElement | null = null;

function captureDialogTrigger(): void {
  dialogTrigger =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
}

function openDocumentation(row: PublicDocumentation): void {
  captureDialogTrigger();
  activeUuid.value = row.uuid;
  activeSummary.value = row;
  isDialogOpen.value = true;
}

function closeDocumentation(): void {
  isDialogOpen.value = false;
  const trigger = dialogTrigger;
  dialogTrigger = null;
  if (trigger) void nextTick(() => trigger.focus({ preventScroll: true }));
}

const documentations = computed(() =>
  getDocumentationRows(data.value ?? {}).slice(0, 3),
);

// Empty only on a successful request that returned nothing. A rejection leaves
// `data` null as well, and calling that "nothing published yet" would state
// something about the data that was never established.
const isEmpty = computed(
  () => !error.value && data.value != null && documentations.value.length === 0,
);

const imageElements = new Map<string, HTMLImageElement>();
const failedImages = ref(new Set<string>());

const setImageElement = (
  uuid: string,
  element: Element | ComponentPublicInstance | null,
): void => {
  if (element instanceof HTMLImageElement) imageElements.set(uuid, element);
  else imageElements.delete(uuid);
};

const markImageError = (uuid: string): void => {
  failedImages.value = new Set(failedImages.value).add(uuid);
};

// Photos that already failed before hydration never emit `error`.
onMounted(() => {
  for (const [uuid, image] of imageElements) {
    if (image.complete && image.naturalWidth === 0) markImageError(uuid);
  }
});
</script>

<style scoped>
.gr-docs__sheet {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

@media (max-width: 959px) {
  .gr-docs__sheet {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 599px) {
  .gr-docs__sheet {
    grid-template-columns: minmax(0, 1fr);
  }
}

.gr-docs__frame {
  min-width: 0;
  background-color: var(--gr-surface);
  border-radius: 4px;
  overflow: hidden;
}

button.gr-docs__link {
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.gr-docs__link {
  display: block;
  height: 100%;
  color: var(--gr-ink);
  text-decoration: none;
  transition: background-color 180ms cubic-bezier(0.2, 0, 0, 1);
}

@media (hover: hover) {
  .gr-docs__link:hover {
    background-color: #f3f6f1;
  }

  .gr-docs__link:hover .gr-docs__title {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.gr-docs__media {
  aspect-ratio: 16 / 11;
  overflow: hidden;
  background-color: #e8eee6;
}

.gr-docs__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gr-docs__img--fallback {
  background-color: #e8eee6;
}

.gr-docs__caption {
  padding: 16px;
}

.gr-docs__title {
  margin: 0 0 6px;
  color: var(--gr-ink);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

.gr-docs__meta {
  margin: 0;
  color: var(--gr-ink-3);
  font-size: 0.8125rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.gr-docs__cta {
  margin-top: 2rem;
}
</style>
